import styles from './breadcrumb.module.scss';

export type BreadcrumbItem = {
  label: string;
  href?: string;
  onClick?: () => void;
};

export type BreadcrumbProps = {
  items: BreadcrumbItem[];
  /** Render function for links - allows framework-specific routing (e.g., Next.js Link) */
  renderLink?: (props: {
    href: string;
    className: string;
    children: React.ReactNode;
  }) => React.ReactNode;
};

/**
 * Breadcrumb navigation component for showing page hierarchy.
 * Pass a custom `renderLink` to integrate with framework-specific routers.
 */
export function Breadcrumb({ items, renderLink }: BreadcrumbProps) {
  const renderBreadcrumbLink = (item: BreadcrumbItem) => {
    if (renderLink && item.href) {
      return renderLink({
        href: item.href,
        className: styles.link ?? '',
        children: item.label
      });
    }

    if (item.href) {
      return (
        <a href={item.href} className={styles.link}>
          {item.label}
        </a>
      );
    }

    if (item.onClick) {
      return (
        <button type="button" className={styles.link} onClick={item.onClick}>
          {item.label}
        </button>
      );
    }

    return <span className={styles.text}>{item.label}</span>;
  };

  return (
    <nav className={styles.breadcrumb} aria-label="Breadcrumb">
      <ol className={styles.list}>
        {items.map((item, index) => (
          <li key={index} className={styles.item}>
            {renderBreadcrumbLink(item)}
            {index < items.length - 1 && (
              <span className={styles.separator} aria-hidden="true">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </span>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}
